import { useEffect, useId, type ReactNode } from 'react';
import { Ayuda } from './Ayuda';

/** Diálogo modal accesible: overlay, cierre por Escape y por click fuera.
 *  `bloqueante` desactiva ambos cierres accidentales (solo la ✕ o los botones del
 *  contenido cierran) — para formularios largos donde perder lo escrito duele.
 *  `ancho` amplía el diálogo ('xl' para redacción).
 *  `ayuda` agrega un ⓘ junto al título (fuera del h2, para no ensuciar el nombre accesible). */
export function Modal({
  titulo,
  onCerrar,
  children,
  bloqueante = false,
  ancho = 'md',
  ayuda,
}: {
  titulo: string;
  onCerrar: () => void;
  children: ReactNode;
  bloqueante?: boolean;
  ancho?: 'md' | 'xl';
  ayuda?: string;
}) {
  const tituloId = useId();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !bloqueante) onCerrar();
    };
    window.addEventListener('keydown', onKey);
    // Bloquea el scroll del fondo mientras el modal está abierto.
    const overflowPrevio = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflowPrevio;
    };
  }, [onCerrar, bloqueante]);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto p-0 sm:items-center sm:p-4">
      <div
        className="fixed inset-0 bg-azul-noche/50"
        onClick={bloqueante ? undefined : onCerrar}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={tituloId}
        className={`relative w-full rounded-t-2xl border border-slate-200 bg-white p-6 shadow-xl sm:rounded-2xl sm:p-7 ${
          ancho === 'xl' ? 'max-w-3xl' : 'max-w-md'
        }`}
      >
        <div className="mb-6 flex items-start justify-between gap-4">
          <div className="flex min-w-0 items-center gap-1">
            <h2 id={tituloId} className="text-lg font-medium tracking-tight text-azul-noche">
              {titulo}
            </h2>
            {ayuda && <Ayuda texto={ayuda} />}
          </div>
          <button
            type="button"
            onClick={onCerrar}
            aria-label="Cerrar"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gris transition-colors duration-150 hover:bg-slate-100 hover:text-azul-profundo focus-visible:outline focus-visible:outline-2 focus-visible:outline-azul-profundo"
          >
            <svg
              viewBox="0 0 24 24"
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="m6 6 12 12M18 6 6 18" />
            </svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
